import { segment } from '@actions/analytics';
import { TEST_SERIES_TAB } from '@cont/SeeAll/TestSeriesListing/Common/TestSeriesTabSwitcher';
import {
  ALL_TEST_SERIES,
  TEST_TEST_SERIES_VIEWED,
  TEST_TESTS_SECTION_CLICKED,
  TEST_TESTS_SUB_SECTION_CLICKED,
  USER_FREE_QUOTA_REACHED
} from '@constants/segment';
import { getPrimeGlobalProps } from '@stores/prime-quota/selectors';
import { localStorageAvailable } from '@utils/localStorage';

const TEST_TAB_ANALYTICS_CONTEXT_KEY = 'test_tab_analytics_context';

const sanitized = (value, fallback = 'NA') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  return String(value);
};

const TEST_SECTION_LABELS = {
  [TEST_SERIES_TAB.ALL]: 'All Tests',
  [TEST_SERIES_TAB.MY]: 'My Tests'
};

const ALL_TESTS_SUBSECTION_LABELS = {
  upcoming: 'Upcoming Tests',
  ongoing: 'Ongoing Tests',
  previous_year: 'Previous Year Tests',
  past: 'Past Tests',
  educator: 'Educator Tests',
  all_series: 'All Test Series'
};

const MY_TESTS_SUBSECTION_LABELS = {
  your_upcoming: 'Your upcoming tests',
  your_attempt: 'Your Attempt'
};

const getTestSectionName = (view) => TEST_SECTION_LABELS[view] || 'All Tests';

const getTestSubsectionMeta = (view, categoryTabId) => {
  const labels =
    view === TEST_SERIES_TAB.MY
      ? MY_TESTS_SUBSECTION_LABELS
      : ALL_TESTS_SUBSECTION_LABELS;
  const entries = Object.entries(labels);
  const index = entries.findIndex(([id]) => id === categoryTabId);

  return {
    name: labels[categoryTabId] || categoryTabId,
    rank: index >= 0 ? index + 1 : 1
  };
};

const mapAppliedFiltersToEventProps = (appliedSelections) => {
  const status = appliedSelections?.status?.[0];
  const testType = appliedSelections?.test_type?.[0];
  const educators = appliedSelections?.educator || [];

  return {
    test_status_selected: status || 'All',
    test_type_selected: testType || 'All',
    no_of_educators_filtered:
      educators.length > 0 ? String(educators.length) : 'NA'
  };
};

export const buildTestTabBaseProps = ({
  view,
  categoryTabId,
  appliedSelections
}) => {
  const subsection = getTestSubsectionMeta(view, categoryTabId);

  return {
    test_section_name: getTestSectionName(view),
    current_tab: 'Tests',
    test_subsection_name: subsection.name,
    test_subsection_rank: subsection.rank,
    ...mapAppliedFiltersToEventProps(appliedSelections)
  };
};

export const saveTestTabAnalyticsContext = (context) => {
  if (!localStorageAvailable()) return;
  localStorage.setItem(TEST_TAB_ANALYTICS_CONTEXT_KEY, JSON.stringify(context));
};

const getTestTabAnalyticsContext = () => {
  if (!localStorageAvailable()) return {};
  try {
    return JSON.parse(
      localStorage.getItem(TEST_TAB_ANALYTICS_CONTEXT_KEY) || '{}'
    );
  } catch {
    return {};
  }
};

const getTestTabEventProps = (overrides = {}) => ({
  ...getTestTabAnalyticsContext(),
  ...overrides,
  ...getPrimeGlobalProps()
});

export const trackTestTestsSectionClicked = (props) => {
  const eventProps = getTestTabEventProps(props);
  saveTestTabAnalyticsContext(eventProps);

  segment.track(TEST_TESTS_SECTION_CLICKED, {
    goal_uid: sanitized(props.goalUid),
    goal_name: sanitized(props.goalName),
    test_section_name: sanitized(eventProps.test_section_name),
    ...getPrimeGlobalProps()
  });
};

export const trackTestTestsSubSectionClicked = (props) => {
  const eventProps = getTestTabEventProps(props);
  saveTestTabAnalyticsContext(eventProps);

  segment.track(TEST_TESTS_SUB_SECTION_CLICKED, {
    goal_uid: sanitized(props.goalUid),
    goal_name: sanitized(props.goalName),
    test_section_name: sanitized(eventProps.test_section_name),
    current_tab: sanitized(eventProps.current_tab, 'Tests'),
    test_subsection_name: sanitized(eventProps.test_subsection_name),
    test_subsection_rank: eventProps.test_subsection_rank,
    ...getPrimeGlobalProps()
  });
};

export const trackTestTestSeriesViewed = (props) => {
  const eventProps = getTestTabEventProps(props);
  saveTestTabAnalyticsContext(eventProps);

  segment.track(TEST_TEST_SERIES_VIEWED, {
    goal_uid: sanitized(props.goalUid),
    goal_name: sanitized(props.goalName),
    test_section_name: sanitized(eventProps.test_section_name),
    current_tab: sanitized(eventProps.current_tab, 'Tests'),
    test_subsection_name: sanitized(eventProps.test_subsection_name),
    test_subsection_rank: eventProps.test_subsection_rank,
    test_status_selected: sanitized(eventProps.test_status_selected),
    test_type_selected: sanitized(eventProps.test_type_selected),
    no_of_educators_filtered: sanitized(eventProps.no_of_educators_filtered),
    test_series_title: sanitized(eventProps.test_series_title),
    test_series_uid: sanitized(eventProps.test_series_uid),
    ...getPrimeGlobalProps()
  });
};

export const getTestTabContextForQuizEvents = () => {
  const context = getTestTabAnalyticsContext();
  if (!context.test_section_name) return {};

  return {
    test_section_name: context.test_section_name,
    current_tab: context.current_tab || 'Tests',
    test_subsection_name: context.test_subsection_name,
    test_subsection_rank: context.test_subsection_rank,
    test_status_selected: context.test_status_selected,
    test_type_selected: context.test_type_selected,
    no_of_educators_filtered: context.no_of_educators_filtered,
    test_series_title: context.test_series_title,
    test_series_uid: context.test_series_uid,
    test_uid: context.test_uid,
    test_name: context.test_name,
    ...getPrimeGlobalProps()
  };
};

export const getTestTabSearchEventProps = (lastPrimarySource) => {
  if (lastPrimarySource !== ALL_TEST_SERIES) return {};

  const context = getTestTabAnalyticsContext();
  if (!context.test_section_name) {
    return { current_tab: 'Tests' };
  }

  return {
    test_section_name: context.test_section_name,
    current_tab: context.current_tab || 'Tests',
    test_subsection_name: context.test_subsection_name,
    test_subsection_rank: context.test_subsection_rank,
    ...getPrimeGlobalProps()
  };
};

export const getTestTabReportEventProps = (quizData = {}) => {
  const context = getTestTabAnalyticsContext();
  const primeGlobalProps = getPrimeGlobalProps();

  if (!context.test_section_name && !quizData?.uid) {
    return primeGlobalProps;
  }

  return {
    test_section_name: sanitized(context.test_section_name, 'NA'),
    current_tab: sanitized(context.current_tab, 'Tests'),
    test_subsection_name: sanitized(context.test_subsection_name),
    test_subsection_rank: context.test_subsection_rank,
    test_status_selected: sanitized(context.test_status_selected),
    test_type_selected: sanitized(context.test_type_selected),
    no_of_educators_filtered: sanitized(context.no_of_educators_filtered),
    test_series_title: sanitized(
      context.test_series_title || quizData?.collection?.programme?.name
    ),
    test_series_uid: sanitized(
      context.test_series_uid || quizData?.collection?.programme?.uid
    ),
    test_uid: sanitized(context.test_uid || quizData?.uid),
    test_name: sanitized(context.test_name || quizData?.title),
    ...primeGlobalProps
  };
};

export const trackUserFreeQuotaReached = ({ limitType, goalUid, goalName }) => {
  segment.track(USER_FREE_QUOTA_REACHED, {
    duration_limit: 'Daily',
    limit_type: limitType,
    goal_uid: sanitized(goalUid),
    goal_name: sanitized(goalName),
    ...getPrimeGlobalProps()
  });
};
